import React, { PropTypes } from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import isEqual from 'lodash/isEqual';

import { shelfShape, accountShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { shallowEquals } from '@bibliocommons/utils-immutable';

import { currentUserSelector, currentAccountSelector } from '../selectors/AuthSelector';
import { getRequestedUser } from '../selectors/UserSelector';
import { getCommentsByUsers, getCommentsTransactions } from '../selectors/ugc/CommentsSelector';
import { getQuotationsByUsers, getQuotationsTransactions } from '../selectors/ugc/QuotationsSelector';
import { getRatingsByUsers, getRatingsTransactions } from '../selectors/ugc/RatingsSelector';
import { getTagsByUsers, getTagsTransactions } from '../selectors/ugc/TagsSelector';
import { getVideosByUsers, getVideosTransactions } from '../selectors/ugc/VideosSelector';
import { getIOwnThisByUsers, getIOwnThisTransactions } from '../selectors/ugc/IOwnThisSelector';
import { getSimilarTitlesByUsers, getSimilarTitlesTransactions } from '../selectors/ugc/SimilarTitlesSelector';
import { getPrivateNotesByUsers, getPrivateNotesTransactions } from '../selectors/ugc/PrivateNotesSelector';
import { currentLibrarySelector } from '../selectors/LibrarySelector';
import { getHiddenNotices } from '../selectors/NoticeSelector';
import DocumentHeadContainer from './DocumentHeadContainer';
import Shelf from '../components/shelves/Shelf';
import FeatureFlagContainer from './FeatureFlagContainer';
import OptInContainer from './OptInContainer';
import BasePageContainer from './BasePageContainer';

import {
  getActiveShelfFilters,
  getShelfOwner,
  getRequestedShelf,
  getShelvesSummary,
  getImportResult,
  getShelfTransactions,
  getShelvesForCurrentUser
} from '../selectors/ShelfSelector';

import AvailabilityDetailActions from '../actions/AvailabilityDetailActions';
import CommentsActions from '../actions/CommentsActions';
import DrawerActions from '../actions/DrawerActions';
import QuotationsActions from '../actions/QuotationsActions';
import RatingsActions from '../actions/RatingsActions';
import TagsActions from '../actions/TagsActions';
import VideosActions from '../actions/VideosActions';
import IOwnThisActions from '../actions/IOwnThisActions';
import NoticeActions from '../actions/NoticeActions';
import SimilarTitlesActions from '../actions/SimilarTitlesActions';
import PrivateNotesActions from '../actions/PrivateNotesActions';
import ShelfActions from '../actions/ShelfActions';
import WorkflowActions from '../actions/WorkflowActions';

export class ShelfContainer extends BasePageContainer {
  static fetchInitialData(params, query, store) {
    const user = params.userId ? getRequestedUser(store.getState()) : currentUserSelector(store.getState());
    const userId = user && user.get('id');

    if (userId && params.shelfName) {
      return [store.dispatch(ShelfActions.searchUserShelf(params.shelfName, userId, query))];
    }

    return [];
  }

  getBackToTitleKey() {
    const { params } = this.props;
    return `${params.shelfName}_shelf`;
  }

  componentWillReceiveProps(nextProps) {
    const { location, owner, currentUser } = this.props;
    const { location: nextLocation, params: nextParams, currentUser: nextCurrentUser } = nextProps;

    if (!isEqual(location, nextLocation) || !shallowEquals(currentUser, nextCurrentUser)) {
      this.props.shelfActions.searchUserShelf(nextParams.shelfName, owner.get('id'), nextLocation.query);
    }
  }

  getPrintUrl() {
    const { location, params, owner } = this.props;
    return `/v2/print/users/${owner.get('id')}/shelves/${params.shelfName}${location.search}`;
  }

  renderOptIn() {
    return (
      <OptInContainer
        optedIn
        bundleName="nerf_shelves"
        allowDismiss={false}
        showAdditionalMessage
        location={this.props.location} />
    );
  }

  render() {
    const {
      activeFilters,
      currentAccount,
      currentLibrary,
      drawer,
      intl,
      isFetching,
      owner,
      shelf,
      summary,
      bibs,
      commentsTransactions,
      quotationsTransactions,
      ratingsTransactions,
      tagsTransactions,
      videosTransactions,
      iOwnThisTransactions,
      similarTitlesTransactions,
      privateNotesTransactions,
      shelfTransactions,
      localBranch,
      importResult,
      ownerComments,
      ownerQuotations,
      ownerRatings,
      ownerTags,
      ownerVideos,
      iOwnThisSettings,
      ownerSimilarTitles,
      ownerPrivateNotes,
      hiddenNotices,
      currentUserShelves
    } = this.props;

    // Actions
    const {
      availabilityDetailActions,
      commentsActions,
      drawerActions,
      quotationsActions,
      ratingsActions,
      tagsActions,
      videosActions,
      iOwnThisActions,
      noticeActions,
      similarTitlesActions,
      privateNotesActions,
      shelfActions,
      workflowActions
    } = this.props;

    const pageTitleId = `section_title_users_${shelf.get('name')}_shelf`;
    const pageTitle = intl.formatMessage({ id: pageTitleId }, { username: owner.get('name') });

    return (
      <div>
        <DocumentHeadContainer pageTitle={pageTitle} />

        <FeatureFlagContainer>
          {(flags) => flags.get('nerfShelves') ? null : this.renderOptIn()}
        </FeatureFlagContainer>

        <Shelf
          activeFilters={activeFilters}
          currentAccount={currentAccount}
          currentLibrary={currentLibrary}
          drawer={drawer}
          isFetching={isFetching}
          owner={owner}
          pageTitle={pageTitle}
          printURL={this.getPrintUrl()}
          shelf={shelf}
          summary={summary}
          bibs={bibs}
          commentsTransactions={commentsTransactions}
          quotationsTransactions={quotationsTransactions}
          ratingsTransactions={ratingsTransactions}
          tagsTransactions={tagsTransactions}
          videosTransactions={videosTransactions}
          iOwnThisTransactions={iOwnThisTransactions}
          shelfTransactions={shelfTransactions}
          similarTitlesTransactions={similarTitlesTransactions}
          privateNotesTransactions={privateNotesTransactions}
          localBranch={localBranch}
          importResult={importResult}
          ownerComments={ownerComments}
          ownerQuotations={ownerQuotations}
          ownerRatings={ownerRatings}
          ownerTags={ownerTags}
          ownerVideos={ownerVideos}
          iOwnThisSettings={iOwnThisSettings}
          ownerSimilarTitles={ownerSimilarTitles}
          ownerPrivateNotes={ownerPrivateNotes}
          hiddenNotices={hiddenNotices}
          availabilityDetailActions={availabilityDetailActions}
          commentsActions={commentsActions}
          quotationsActions={quotationsActions}
          drawerActions={drawerActions}
          ratingsActions={ratingsActions}
          tagsActions={tagsActions}
          videosActions={videosActions}
          iOwnThisActions={iOwnThisActions}
          noticeActions={noticeActions}
          similarTitlesActions={similarTitlesActions}
          privateNotesActions={privateNotesActions}
          shelfActions={shelfActions}
          workflowActions={workflowActions}
          currentUserShelves={currentUserShelves} />
        {this.props.children}
      </div>
    );
  }
}

const drawerShape = ImmutablePropTypes.contains({
  open: PropTypes.bool
});

const importResultShape = ImmutablePropTypes.mapContains({
  count: PropTypes.number,
  status: PropTypes.string
});

ShelfContainer.propTypes = {
  currentAccount: accountShape,
  importResult: importResultShape,
  localBranch: PropTypes.string,
  currentLibrary: ImmutablePropTypes.map.isRequired,
  currentUser: ImmutablePropTypes.map,
  drawer: drawerShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  owner: userIdentityShape.isRequired,
  shelf: shelfShape.isRequired,
  activeFilters: ImmutablePropTypes.map.isRequired,
  summary: ImmutablePropTypes.map.isRequired,
  bibs: ImmutablePropTypes.map.isRequired,
  ownerRatings: ImmutablePropTypes.map.isRequired,
  ownerComments: ImmutablePropTypes.map.isRequired,
  ownerQuotations: ImmutablePropTypes.map.isRequired,
  ownerTags: ImmutablePropTypes.map.isRequired,
  ownerVideos: ImmutablePropTypes.map.isRequired,
  iOwnThisSettings: ImmutablePropTypes.map.isRequired,
  ownerSimilarTitles: ImmutablePropTypes.map.isRequired,
  ownerPrivateNotes: ImmutablePropTypes.map.isRequired,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  ratingsTransactions: ImmutablePropTypes.map.isRequired,
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  iOwnThisTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  privateNotesTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  hiddenNotices: ImmutablePropTypes.set.isRequired,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ratingsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  privateNotesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  noticeActions: PropTypes.objectOf(PropTypes.func).isRequired,
  iOwnThisActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  workflowActions: PropTypes.objectOf(PropTypes.func).isRequired,
  intl: intlShape.isRequired,
  currentUserShelves: ImmutablePropTypes.map.isRequired
};

const mapStateToProps = (state) => {
  const owner = getShelfOwner(state);
  const ownerId = owner.get('id');

  return {
    owner,
    currentUserShelves: getShelvesForCurrentUser(state),
    activeFilters: getActiveShelfFilters(state),
    currentAccount: currentAccountSelector(state),
    currentLibrary: currentLibrarySelector(state),
    currentUser: currentUserSelector(state),
    drawer: state.get('drawer'),
    bibs: state.getIn(['entities', 'bibs'], Immutable.Map()),
    isFetching: state.getIn(['shelves', 'shelfSearch', 'isFetching']),
    localBranch: state.getIn(['app', 'localBranch']),
    shelf: getRequestedShelf(state),
    summary: getShelvesSummary(state),
    ownerSimilarTitles: getSimilarTitlesByUsers(state).get(ownerId, Immutable.Map()),
    ownerComments: getCommentsByUsers(state).get(ownerId, Immutable.Map()),
    ownerQuotations: getQuotationsByUsers(state).get(ownerId, Immutable.Map()),
    ownerRatings: getRatingsByUsers(state).get(ownerId, Immutable.Map()),
    ownerTags: getTagsByUsers(state).get(ownerId, Immutable.Map()),
    ownerVideos: getVideosByUsers(state).get(ownerId, Immutable.Map()),
    ownerPrivateNotes: getPrivateNotesByUsers(state).get(ownerId, Immutable.Map()),
    iOwnThisSettings: getIOwnThisByUsers(state).get(ownerId, Immutable.Map()),
    commentsTransactions: getCommentsTransactions(state),
    quotationsTransactions: getQuotationsTransactions(state),
    ratingsTransactions: getRatingsTransactions(state),
    tagsTransactions: getTagsTransactions(state),
    videosTransactions: getVideosTransactions(state),
    shelfTransactions: getShelfTransactions(state),
    iOwnThisTransactions: getIOwnThisTransactions(state),
    similarTitlesTransactions: getSimilarTitlesTransactions(state),
    privateNotesTransactions: getPrivateNotesTransactions(state),
    importResult: getImportResult(state),
    hiddenNotices: getHiddenNotices(state)
  };
};

const mapDispatchToProps = dispatch => ({
  availabilityDetailActions: bindActionCreators(AvailabilityDetailActions, dispatch),
  commentsActions: bindActionCreators(CommentsActions, dispatch),
  quotationsActions: bindActionCreators(QuotationsActions, dispatch),
  videosActions: bindActionCreators(VideosActions, dispatch),
  iOwnThisActions: bindActionCreators(IOwnThisActions, dispatch),
  drawerActions: bindActionCreators(DrawerActions, dispatch),
  ratingsActions: bindActionCreators(RatingsActions, dispatch),
  tagsActions: bindActionCreators(TagsActions, dispatch),
  noticeActions: bindActionCreators(NoticeActions, dispatch),
  shelfActions: bindActionCreators(ShelfActions, dispatch),
  similarTitlesActions: bindActionCreators(SimilarTitlesActions, dispatch),
  privateNotesActions: bindActionCreators(PrivateNotesActions, dispatch),
  workflowActions: bindActionCreators(WorkflowActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(ShelfContainer));



// WEBPACK FOOTER //
// ./app/containers/ShelfContainer.jsx